import { IconInfo } from "@/assets/icons/geist/IconInfo";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import type { SecretMountType } from "@/queries/secret-queries";
import type { SecretScope } from "gitpod-next-api/gitpod/v1/secret_pb";
import { Check } from "lucide-react";
import { useCallback, useEffect, useId, useState, type FC } from "react";

type RegistryCredentials = {
    username: string;
    password: string;
};

type SecretTypeOption = {
    value: SecretMountType["case"];
    label: string;
    description: string;
};

const SECRET_TYPE_OPTIONS: readonly SecretTypeOption[] = [
    {
        value: "filePath",
        label: "File",
        description: "Store in a file that will be mounted into your workspace",
    },
    {
        value: "environmentVariable",
        label: "Environment variable",
        description: "Simple key-value pairs",
    },
    {
        value: "containerRegistryBasicAuthHost",
        label: "Container registry",
        description:
            "Store credentials for container registries like Docker Hub, GitHub Container Registry, and other registries that use basic authentication",
    },
] as const;

export type NewSecretFormOnSubmitInput = {
    secretName: string;
    secretValuePlaintext: string;
    mount: SecretMountType;
};

type MountOption = SecretMountType["case"];

type NewSecretFormProps = {
    formName: string;
    scope: SecretScope;
    enableSecretContainerRegistry: boolean;
    onDisabledChange: (disabled: boolean) => void;
    onSubmit: (values: NewSecretFormOnSubmitInput) => Promise<void>;
};

export const NewSecretForm: FC<NewSecretFormProps> = ({
    formName,
    enableSecretContainerRegistry,
    onSubmit,
    onDisabledChange,
}) => {
    const nameId = useId();
    const secretId = useId();
    const mountId = useId();
    const filePathId = useId();
    const registryHostId = useId();
    const registryUsernameId = useId();
    const registryPasswordId = useId();

    const [name, setName] = useState<string>("");
    const [secret, setSecret] = useState<string>("");
    const [mountOption, setMountOption] = useState<MountOption>("filePath");
    const [filePath, setFilePath] = useState<string>("/usr/local/secrets/");
    const [registryHost, setRegistryHost] = useState<string>("");
    const [registryCredentials, setRegistryCredentials] = useState<RegistryCredentials>({
        username: "",
        password: "",
    });
    const [isEcrRegistry, setIsEcrRegistry] = useState<boolean>(false);
    const [isGarRegistry, setIsGarRegistry] = useState<boolean>(false);

    // Check if the registry is an ECR registry
    const checkIfEcrRegistry = useCallback((host: string) => {
        const isEcr = host.includes(".dkr.ecr.") && host.includes(".amazonaws.com");
        setIsEcrRegistry(isEcr);

        // Auto-fill credentials if ECR is detected
        if (isEcr) {
            setRegistryCredentials({
                username: "runner-native",
                password: "runner-native",
            });
        }
    }, []);

    // Check if the registry is a GAR registry
    const checkIfGarRegistry = useCallback((host: string) => {
        const isGar = host.endsWith("-docker.pkg.dev");
        setIsGarRegistry(isGar);

        // Auto-fill credentials if GAR is detected
        if (isGar) {
            setRegistryCredentials({
                username: "runner-native",
                password: "runner-native",
            });
        }
    }, []);

    const onUpdateName = useCallback(
        (value: string) => {
            setName(value);
            setFilePath((path) => {
                const parts = path.split("/");
                parts.pop();
                parts.push(value.replace(/[^a-zA-Z0-9_]/g, ""));
                return parts.join("/");
            });
        },
        [setName, setFilePath],
    );

    const handleRegistryHostChange = useCallback(
        (value: string) => {
            setRegistryHost(value);
            checkIfEcrRegistry(value);
            checkIfGarRegistry(value);
        },
        [checkIfEcrRegistry, checkIfGarRegistry],
    );

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            let mount: SecretMountType = {
                case: "environmentVariable",
                value: true,
            };

            if (mountOption === "filePath") {
                mount = {
                    case: "filePath",
                    value: filePath,
                };
            } else if (mountOption === "containerRegistryBasicAuthHost") {
                mount = {
                    case: "containerRegistryBasicAuthHost",
                    value: registryHost,
                };
            }

            await onSubmit({
                secretName: name,
                secretValuePlaintext:
                    mountOption === "containerRegistryBasicAuthHost"
                        ? btoa(`${registryCredentials.username}:${registryCredentials.password}`)
                        : secret,
                mount,
            });
        },
        [onSubmit, name, secret, mountOption, filePath, registryHost, registryCredentials],
    );

    useEffect(() => {
        const isValid = () => {
            if (mountOption === "containerRegistryBasicAuthHost") {
                return name && registryHost && registryCredentials.username && registryCredentials.password;
            }
            return name && secret;
        };

        onDisabledChange(!isValid());
    }, [name, secret, mountOption, registryHost, registryCredentials, onDisabledChange]);

    const renderSecretInput = () => {
        if (mountOption === "containerRegistryBasicAuthHost") {
            return (
                <div className="flex flex-col gap-4">
                    <InputField
                        id={registryHostId}
                        label="Registry hostname"
                        hint="Registry hostname must be a valid hostname or IP address"
                    >
                        <Input
                            id={registryHostId}
                            type="text"
                            name="registry-hostname"
                            value={registryHost}
                            placeholder="https://index.docker.io/v1"
                            onChange={(e) => handleRegistryHostChange(e.target.value)}
                            autoComplete="off"
                            pattern="^(https?:\/\/)?([a-zA-Z0-9][a-zA-Z0-9\.-]*\.[a-zA-Z0-9][a-zA-Z0-9\.-]*)(:[0-9]+)?(\/.+)?$"
                            data-testid="registry-hostname-input"
                        />
                        {isEcrRegistry && (
                            <div className="mt-2 flex flex-row gap-3 rounded-xl border border-border-base bg-surface-glass px-3 py-4">
                                <IconInfo size="base" className="text-blue-500" />
                                <div className="flex w-full grow flex-col">
                                    <p className="text-base">
                                        ECR registry detected. Credentials automatically set to use IAM authentication.
                                    </p>
                                    <p className="text-sm">
                                        <ExternalLink href="https://ona.com/docs/ona/configuration/secrets/container-registry-secret#using-aws-ecr-with-iam-authentication">
                                            Learn more about ECR authentication
                                        </ExternalLink>
                                    </p>
                                </div>
                            </div>
                        )}
                        {isGarRegistry && (
                            <div className="mt-2 flex flex-row gap-3 rounded-xl border border-border-base bg-surface-glass px-3 py-4">
                                <IconInfo size="base" className="text-blue-500" />
                                <div className="flex w-full grow flex-col">
                                    <p className="text-base">
                                        Google Artifact Registry (GAR) registry detected. Credentials automatically set
                                        to use service account authentication.
                                        <ExternalLink href="https://ona.com/docs/ona/configuration/secrets/container-registry-secret#using-google-artifact-registry-with-service-account-authentication">
                                            Learn more about GAR authentication
                                        </ExternalLink>
                                    </p>
                                </div>
                            </div>
                        )}
                    </InputField>
                    <InputField id={registryUsernameId} label="Registry username">
                        <Input
                            id={registryUsernameId}
                            type="text"
                            name="registry-username"
                            value={registryCredentials.username}
                            placeholder="Enter registry username"
                            onChange={(e) => setRegistryCredentials((prev) => ({ ...prev, username: e.target.value }))}
                            autoComplete="off"
                            data-testid="registry-username-input"
                            disabled={isEcrRegistry || isGarRegistry}
                        />
                    </InputField>
                    <InputField id={registryPasswordId} label="Registry password">
                        <Input
                            id={registryPasswordId}
                            type="password"
                            name="registry-password"
                            value={registryCredentials.password}
                            placeholder="Enter registry password"
                            onChange={(e) => setRegistryCredentials((prev) => ({ ...prev, password: e.target.value }))}
                            autoComplete="off"
                            data-testid="registry-password-input"
                            disabled={isEcrRegistry || isGarRegistry}
                        />
                    </InputField>
                </div>
            );
        }

        return (
            <InputField id={secretId} label="Secret">
                <Textarea
                    id={secretId}
                    name="secret-value"
                    rows={5}
                    dontAutosize={true}
                    value={secret}
                    placeholder="Enter your secret"
                    onChange={(e) => setSecret(e.target.value)}
                    className="resize-none"
                    data-testid="secret-value-input"
                />
            </InputField>
        );
    };

    const visibleSecretTypes = SECRET_TYPE_OPTIONS.filter(
        (type) => type.value !== "containerRegistryBasicAuthHost" || enableSecretContainerRegistry,
    );

    return (
        <form
            onSubmit={handleSubmit}
            name={formName}
            id={formName}
            data-testid="new-secret-form"
            className="flex flex-col gap-4"
        >
            <InputField id={mountId} label="Secret type">
                <Select
                    name="secret-type"
                    value={mountOption}
                    onValueChange={(value: string) => setMountOption(value as MountOption)}
                >
                    <SelectTrigger id={mountId} loading={false} data-testid="secret-type-dropdown-button">
                        <SelectValue>{SECRET_TYPE_OPTIONS.find((t) => t.value === mountOption)?.label}</SelectValue>
                    </SelectTrigger>
                    <SelectContent className="w-[var(--radix-select-trigger-width)]">
                        {visibleSecretTypes.map((type) => (
                            <SelectItem
                                key={type.value}
                                value={type.value ?? ""}
                                className="border-b border-border-light last:border-b-0"
                                data-testid={`secret-type-option-${type.value}`}
                            >
                                <div className="flex w-full items-center justify-between">
                                    <div className="min-w-0 flex-1">
                                        <div className="break-words">{type.label}</div>
                                        <div className="break-words text-sm text-content-tertiary">
                                            {type.description}
                                        </div>
                                    </div>
                                    {mountOption === type.value && (
                                        <Check className="ml-2 h-4 w-4 shrink-0 text-content-primary" />
                                    )}
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </InputField>

            <InputField
                label="Name"
                id={nameId}
                hint="Secret names can only contain alphanumeric characters ([a-z], [A-Z], [0-9]) or underscores (_)."
            >
                <Input
                    id={nameId}
                    type="text"
                    name="secret-name"
                    value={name}
                    placeholder="Ex: MY_SECRET"
                    pattern="[a-zA-Z0-9_]+"
                    onChange={(e) => onUpdateName(e.target.value)}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    data-testid="secret-name-input"
                />
            </InputField>

            {mountOption === "filePath" && (
                <InputField id={filePathId} label="File Location">
                    <Input
                        id={filePathId}
                        type="text"
                        name="mount-file-path"
                        value={filePath}
                        placeholder="Ex: /usr/local/secrets/MY_SECRET"
                        onChange={(e) => setFilePath(e.target.value)}
                        className="w-full"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        data-testid="secret-filepath-input"
                    />
                </InputField>
            )}

            {renderSecretInput()}
        </form>
    );
};
